import React, { useEffect, useState } from "react";
import { AppLayout } from "../components/app/AppLayout";
import { notificationsApi } from "../lib/api";
import { toast } from "sonner";
import { Loader2, AlertCircle, CheckCircle2, Eye, Ban, MessageSquare } from "lucide-react";
import { getApiError } from "../lib/utils";

const SEV = {
  LOW: { c: "bg-slate-100 text-slate-700 border-slate-200", icon: "🟡" },
  MEDIUM: { c: "bg-amber-100 text-amber-800 border-amber-200", icon: "🟠" },
  HIGH: { c: "bg-red-100 text-red-800 border-red-200", icon: "🔴" },
  URGENT: { c: "bg-red-600 text-white border-red-700", icon: "🚨" },
};

const STATUS_STYLES = {
  OPEN: "bg-amber-100 text-amber-800 border-amber-200",
  IN_REVIEW: "bg-nmi-100 text-nmi-800 border-nmi-200",
  RESOLVED: "bg-emerald-100 text-emerald-800 border-emerald-200",
  DISMISSED: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("OPEN");
  const [resolving, setResolving] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const d = await notificationsApi.list(filter === "ALL" ? {} : { status_filter: filter });
      setItems(d.items || []);
      setCounts(d.counts || {});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (n, status, resolutionNote) => {
    try {
      await notificationsApi.update(n.notification_id, {
        status,
        resolution_note: resolutionNote ?? undefined,
      });
      toast.success(`Marked as ${status}`);
      setResolving(null);
      setNote("");
      load();
    } catch (e) {
      toast.error(getApiError(e, "Failed"));
    }
  };

  return (
    <AppLayout mobileTitle="Notifications">
      <div className="mb-6">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
          Inbox
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
          Notifications
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Reports filed by NMI staff about specific providers. Triage, resolve, or dismiss.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {["OPEN", "IN_REVIEW", "RESOLVED", "DISMISSED", "ALL"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
              filter === s
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            {s.replace("_", " ")}
            {counts[s] > 0 && (
              <span className={`inline-flex items-center justify-center rounded-full px-1.5 text-[10px] font-bold ${filter === s ? "bg-white/20" : "bg-slate-200 text-slate-700"}`}>
                {counts[s]}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-500">
          <Loader2 size={18} className="inline animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-10 text-center text-sm text-slate-500">
          No notifications in this state.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((n) => {
            const sev = SEV[n.severity] || SEV.MEDIUM;
            const stStyle = STATUS_STYLES[n.status] || STATUS_STYLES.OPEN;
            return (
              <div key={n.notification_id} className="rounded-md border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${sev.c}`}>
                        {sev.icon} {n.severity}
                      </span>
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${stStyle}`}>
                        {n.status.replace("_", " ")}
                      </span>
                      <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">{n.channel}</span>
                    </div>
                    <div className="mt-2 font-semibold text-slate-900">{n.facility_name}</div>
                    <div className="text-[10px] font-mono text-slate-500">{n.facility_id}</div>
                    <p className="mt-2 text-sm text-slate-800">{n.message}</p>
                    <div className="mt-2 text-[11px] text-slate-500">
                      Filed by <b>{n.reporter_name}</b> ({n.reporter_role?.replace("_", " ")}) · <span className="font-mono">{n.created_at?.slice(0, 16).replace("T", " ")}</span>
                    </div>
                    {n.resolution_note && (
                      <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-700">
                        <span className="font-bold uppercase tracking-wider text-slate-500 text-[9px]">Resolution</span>
                        <div className="mt-0.5">{n.resolution_note}</div>
                        {n.resolved_by && <div className="mt-1 text-[10px] text-slate-500">— {n.resolved_by} · {n.resolved_at?.slice(0, 16).replace("T", " ")}</div>}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {n.status === "OPEN" && (
                      <button
                        onClick={() => setStatus(n, "IN_REVIEW")}
                        className="inline-flex items-center gap-1 rounded-md bg-nmi-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-nmi-700"
                      >
                        <Eye size={12} /> Start review
                      </button>
                    )}
                    {n.status !== "RESOLVED" && n.status !== "DISMISSED" && (
                      <button
                        onClick={() => { setResolving(n); setNote(""); }}
                        className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700"
                      >
                        <CheckCircle2 size={12} /> Resolve
                      </button>
                    )}
                    {n.status !== "DISMISSED" && n.status !== "RESOLVED" && (
                      <button
                        onClick={() => { if (window.confirm("Dismiss this notification?")) setStatus(n, "DISMISSED", null); }}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
                      >
                        <Ban size={12} /> Dismiss
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {resolving && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm" onClick={() => setResolving(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-md border border-slate-200 bg-white shadow-2xl">
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="flex items-center gap-2">
                <MessageSquare size={16} className="text-emerald-600" strokeWidth={2.5} />
                <h3 className="font-heading text-base font-bold text-slate-900">Resolve notification</h3>
              </div>
              <p className="mt-1 text-xs text-slate-600">{resolving.facility_name}</p>
            </div>
            <div className="p-5">
              <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Resolution note</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={4}
                className="w-full resize-none rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-nmi-600 focus:ring-2 focus:ring-nmi-600/20"
                placeholder="What action was taken?"
              />
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
              <button onClick={() => setResolving(null)} className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
              <button
                onClick={() => setStatus(resolving, "RESOLVED", note.trim() || null)}
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
              >
                <CheckCircle2 size={12} /> Mark resolved
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
